import { Component, CUSTOM_ELEMENTS_SCHEMA, Inject } from '@angular/core';

import { BankAccountBO, CurrencyCollectionQuery, CurrencyItem } from '@skylab-shared/business';
import { EventBusBase, IdentifiableValueConverter, RepositoryResponseCode, RepositorySaveResponse } from '@skylab-shared/core';
import { BankAccountModel } from '@skylab-shared/dataModel';

import { BankAccountRepository, BankAccountRepositoryBase } from '@skylab-angular-shared/business';
import { CommandBus, EventBus, L10n, L10nBase, RootContext, RootContextBase, Weaver, WeaverBase } from '@skylab-angular-shared/core';
import { BooleanValueAccessorDirective, ButtonComponent, DialogContentComponentIns, DialogHeaderComponentIns, DialogInsService, DialogState, FocusByDirective, FormContainerComponent, SelectValueAccessorDirective, TextValueAccessorDirective, ToastService, ValidateDirective, ValidationSourceDirective } from '@skylab-angular-shared/ui';

import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@skylab-angular-shared/pipes';
import { DictionaryDialogComponentBase, DictionaryDialogData, DictionaryDialogViewModelBase } from '../dictionary-dialog-base';

export interface BankAccountDialogResult {
    id: number;
    version: string;
}

export interface BankAccountDialogParams {
    data: {
        currencyId?: number;
    };
}

interface BankAccountViewModel extends DictionaryDialogViewModelBase<BankAccountBO> {
    currencies: CurrencyItem[];
    currenciesConverter?: IdentifiableValueConverter | null;
}

interface BankAccountDialogData extends DictionaryDialogData {
    currencyId: number;
}

@Component({
    selector: 'skl-bank-account-dialog',
    templateUrl: './bank-account-dialog.component.html',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [DialogContentComponentIns, DialogHeaderComponentIns, FormContainerComponent, ValidationSourceDirective, BooleanValueAccessorDirective, FormsModule, ValidateDirective, SelectValueAccessorDirective, FocusByDirective, TextValueAccessorDirective, ButtonComponent, TranslatePipe]
})
export class BankAccountDialogComponent extends DictionaryDialogComponentBase
<
BankAccountModel,
BankAccountBO,
BankAccountRepositoryBase,
BankAccountViewModel,
BankAccountDialogData
> {
    public constructor(
        @Inject(BankAccountRepository) BankAccountRepository: BankAccountRepositoryBase,
        @Inject(RootContext) rootContext: RootContextBase,
        @Inject(Weaver) weaver: WeaverBase,
        @Inject(L10n) l10n: L10nBase,
        @Inject(EventBus) eventBus: EventBusBase,

        toastService: ToastService,
        commandBus: CommandBus,
        dialogService: DialogInsService,
    ) {
        super(BankAccountRepository, toastService, l10n, weaver, rootContext, commandBus, eventBus, dialogService);
    }

    protected override async prepareViewModelAsync(): Promise<void> {
        await super.prepareViewModelAsync();
        this.viewModel.currencies = await this.localContext.getAsync(CurrencyCollectionQuery);
        this.viewModel.currenciesConverter = new IdentifiableValueConverter(this.viewModel.currencies);

        if (!this.viewModel.bo.model.currencyId) {
            const defaultCurrency = this.viewModel.currencies.find(c => c.default === true);
            this.viewModel.bo.model.currencyId = this.data.currencyId || defaultCurrency!.id;
        }
    }

    protected override getInitialState(): BankAccountViewModel {
        return {
            ...super.getInitialState(),
            currencies: [],
            currenciesConverter: null
        };
    }

    protected override onSaveError(response?: RepositorySaveResponse): void {
        this.toastService.showAsync({
            type: 'error',
            text: this.l10n._T('scenes.configuration.shared.bank-account.messages.save-error')
        });
    }

    protected override onSaveSuccess(response?: RepositorySaveResponse): void {
        if (response?.object) {
            this.dialogService.close<BankAccountDialogResult>({ state: DialogState.Confirm, data: response.object });
        }
    }

    protected override onReload(): void {
        this.dialogService.close({ state: DialogState.Confirm, data: RepositoryResponseCode.OutOfDateData });
    }
}
